/**
 * On-demand game server connect for an event.
 * - POST /vv/events/{id}/connect provisions/joins a server (200 ready, 202 starting).
 * - While starting, poll GET /vv/events/{id}/connect/status until ready.
 *
 * Ported from vv-web-app so the Intersect hub can provision on demand and benefit
 * from idle auto-shutdown. The connect call must succeed before booting Unity.
 */

import { useCallback, useRef } from 'react';
import API from '../API';

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_ATTEMPTS = 72; // ~6 minutes

const FRIENDLY_STARTING = 'Opening the hub…';
const FRIENDLY_ALMOST = 'Almost ready…';
const FRIENDLY_STILL = 'Still getting things ready…';

export interface ConnectResponse {
  status: 'ready' | 'starting' | 'closed';
  server_id?: string;
  instance_id?: string;
  poll_after_ms?: number;
  message?: string;
}

/** Pull a human-readable message from API error JSON or plain text. */
export function parseApiErrorMessage(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return '';
  try {
    const parsed = JSON.parse(trimmed) as { error?: string; message?: string };
    return parsed.error || parsed.message || trimmed;
  } catch {
    return trimmed;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function connectToEvent(
  accessToken: string,
  eventId: string,
  onStatus?: (message: string) => void
): Promise<ConnectResponse> {
  const postResponse = await API.connectEvent(accessToken, { eventId });

  if (postResponse.status === 200) {
    return (await postResponse.json()) as ConnectResponse;
  }

  if (postResponse.status === 403) {
    const text = await postResponse.text();
    try {
      const data = JSON.parse(text) as ConnectResponse;
      if (data.status === 'closed') {
        throw new Error('CLOSED');
      }
    } catch (e) {
      if (e instanceof Error && e.message === 'CLOSED') throw e;
    }
    throw new Error(parseApiErrorMessage(text) || 'This venue is not available right now.');
  }

  if (postResponse.status === 202 || [500, 502, 504].includes(postResponse.status)) {
    onStatus?.(FRIENDLY_STARTING);
    return pollConnectStatus(accessToken, eventId, onStatus);
  }

  const errorText = await postResponse.text();
  throw new Error(parseApiErrorMessage(errorText) || `Could not connect (${postResponse.status})`);
}

async function pollConnectStatus(
  accessToken: string,
  eventId: string,
  onStatus?: (message: string) => void
): Promise<ConnectResponse> {
  let transient503Count = 0;
  let transientServerErrorCount = 0;

  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
    await sleep(POLL_INTERVAL_MS);

    const response = await API.connectEventStatus(accessToken, { eventId });

    if ([500, 502, 504].includes(response.status)) {
      transientServerErrorCount++;
      if (transientServerErrorCount <= 3) {
        onStatus?.(FRIENDLY_STILL);
        continue;
      }
      throw new Error('Could not reach the venue. Please try again shortly.');
    }
    transientServerErrorCount = 0;

    if (response.status === 403) {
      const text = await response.text();
      try {
        const data = JSON.parse(text) as ConnectResponse;
        if (data.status === 'closed') {
          throw new Error('CLOSED');
        }
      } catch (e) {
        if (e instanceof Error && e.message === 'CLOSED') throw e;
      }
      throw new Error(parseApiErrorMessage(text) || 'This venue is not available right now.');
    }

    if (response.status === 200) {
      const data = (await response.json()) as ConnectResponse;
      if (data.status === 'ready') {
        return data;
      }
    }

    if (response.status === 202) {
      transient503Count = 0;
      const data = (await response.json()) as ConnectResponse;
      const msg = data.message || (attempt > 4 ? FRIENDLY_ALMOST : FRIENDLY_STARTING);
      onStatus?.(msg);
      continue;
    }

    if (response.status === 503) {
      const text = await response.text();
      const msg = parseApiErrorMessage(text);
      if (msg.toLowerCase().includes('capacity') || msg.toLowerCase().includes('full')) {
        throw new Error('This venue is full. Please try again in a few minutes.');
      }
      // Transient 503 while the server is still coming online — keep polling.
      transient503Count++;
      if (transient503Count <= 12) {
        onStatus?.(FRIENDLY_STILL);
        continue;
      }
      throw new Error('The venue is taking a moment to get ready. Please refresh and try again.');
    }

    if (!response.ok) {
      const text = await response.text();
      throw new Error(parseApiErrorMessage(text) || `Could not connect (${response.status})`);
    }
  }

  throw new Error('This is taking longer than usual. Please refresh the page and try again.');
}

/** Hook wrapper for components that need connect with a stable callback. */
export function useEventConnect() {
  const inFlightRef = useRef(false);

  const connect = useCallback(
    async (accessToken: string, eventId: string, onStatus?: (message: string) => void) => {
      inFlightRef.current = true;
      try {
        return await connectToEvent(accessToken, eventId, onStatus);
      } finally {
        inFlightRef.current = false;
      }
    },
    []
  );

  return { connect };
}
